import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Row, Col, Button, ButtonGroup, Card, CardBody, CardHeader, CardTitle, Spinner } from "reactstrap";
import ServiceSubmitDateRange from "./ServiceSubmitDateRange";
import ServiceKpiPanel from "./ServiceKpiPanel";
import ServiceReportTable from "./ServiceReportTable";
import { getServiceDashboard } from "API";
import { useWorkingStates } from "contexts/WorkingStatesContext";
import { calcFailPct } from "./serviceUtils";

// ─── Config ───────────────────────────────────────────────────────────────────
const REPLENISHMENT_THRESHOLD = 30;
const TOP_N = 10;
const DEFAULT_MAX_COLS = 5;

const ALL_TYPES = "ALL";

const COLUMN_STATUS = { OK: 1 };

const INSIGHT_FIELDS = Object.freeze({
    SALES:            "buy/rent",
    SWAP:             "swap",
    SWAP_PAID:        "swap_paid",
    SWAP_FAILS:       "swap_fails",
    RETURN:           "total_return",   // backend pre-computes: 412 + 413
    MANUAL_RETURN:    "manual_return",  // contact_information.is_returned = 1
    MANUAL_FAIL:      "manual_fail",   // contact_information.is_returned = 0
    FAILS:            "fails",          // logs_lvl 207 – device not returned
    QR_SCANS:         "qr_scans",
    RESETS:           "resets",
    LOCATION_NAME:    "location_name",
    WORKING_STATE_ID: "working_state_id",
});

// Keys of the normalized machine object built in transformInsightsData
const MACHINE_KEYS = Object.freeze({
    MACHINE:       "machine",
    SALES:         "sales",
    SWAPS:         "swaps",
    RETURNS:       "returns",      // total_return (412+413) – KPI and % denominator
    MANUAL_RETURN: "manualReturn", // contact_information.is_returned = 1
    MANUAL_FAIL:   "manualFail",  // contact_information.is_returned = 0
    FAILS:         "fails",        // 207 – fail tables
    COUNT:         "count",
    ERRORS:        "errors",
    ERROR_PCT:     "errorPct",
});

// Field names from the /top-offline endpoint response and the mapped row key
const TOP_OFFLINE_FIELDS = Object.freeze({
    NAME:          "name",
    OFFLINE_COUNT: "offline_count",
    OFFLINE_EVENTS: "offlineEvents", // mapped key used in table rows
});

// ─── Optional color palette ───────────────────────────────────────────────────
// Shortcuts for repeated color values – use in TABLE_THEMES instead of
// duplicating the same hex. Usage is optional.

const COLORS = {
    light:      "#3d3e50",
    superlight: "#525464",
    error: "#f5365c",
};

// ─── Color themes – single source of truth for table colors ──────────────────
// Each theme can contain any combination of the following fields:
//   colorBackgroundHeader  – card header background
//   colorTextHeader        – card header text color
//   colorBackgroundOdd     – odd row background
//   colorBackgroundEven    – even row background
//   colorTextOdd           – odd row text color
//   colorTextEven          – even row text color
//   colorSubtitle          – subtitle text color
//   colorDescription       – description text color
// "default" – no colors, uses the app's original theme
const TABLE_THEMES = {
    default:        {},
    top10Sales:     { colorBackgroundHeader: "#00A896", colorBackgroundOdd: COLORS.superlight },
    top10Swaps:     { colorBackgroundHeader: "#3f9a1b", colorBackgroundOdd: COLORS.superlight },
    top10Offline:   { colorBackgroundHeader: "#6a1b9a", colorBackgroundOdd: COLORS.superlight },
    returnFailures: { colorBackgroundHeader: "#e65100", colorBackgroundOdd: COLORS.superlight },
    manualSuccess:  { colorBackgroundHeader: "#1565c0", colorBackgroundOdd: COLORS.superlight },
    replenishment:  { colorBackgroundHeader: "#c62828", colorBackgroundOdd: COLORS.superlight },
    watchList:      { colorBackgroundHeader: "#e65100", colorBackgroundOdd: COLORS.superlight },
};

// ─── Column definitions ───────────────────────────────────────────────────────

const MACHINE_COL = { header: "Machine", accessor: MACHINE_KEYS.MACHINE };

const top10SalesColumns    = [MACHINE_COL, { header: "Sales",          accessor: MACHINE_KEYS.SALES }];
const top10SwapsColumns    = [MACHINE_COL, { header: "Swaps",          accessor: MACHINE_KEYS.SWAPS }];
const top10OfflineColumns  = [MACHINE_COL, { header: "Offline Events", accessor: TOP_OFFLINE_FIELDS.OFFLINE_EVENTS }];
const manualReturnsColumns = [MACHINE_COL, { header: "Count", accessor: MACHINE_KEYS.COUNT }];

const returnFailuresColumns = [
    MACHINE_COL,
    { header: "Errors", accessor: MACHINE_KEYS.ERRORS },
    { header: "Error",  accessor: MACHINE_KEYS.ERROR_PCT, unit: "%" },
];

// ─── Device data processing (replenishment + watchList) ──────────────────────

// Transforms raw /machines/info response into replenishment and watchList tables.
// Detects column count dynamically (column1_units, column2_units, ...).
// Replenishment: machines with total_units below threshold.
// Watch list: machines with 2+ columns at NOK status.
const processDevicesData = (devicesData) => {
    // Count the highest number of column slots across all machines
    const maxCols = devicesData.reduce((max, machine) => {
        let n = 0;
        while (machine[`column${n + 1}_units`] !== undefined) n++;
        return Math.max(max, n);
    }, 0);

    const replenishment = devicesData
        .filter((d) => d.total_units < REPLENISHMENT_THRESHOLD)
        .map((d) => {
            const row = { machine: d.name || d.serial };
            for (let i = 1; i <= maxCols; i++) row[`c${i}`] = d[`column${i}_units`] ?? 0;
            row.total = d.total_units;
            return row;
        });

    const watchList = devicesData
        .map((d) => {
            const criticalCols = [];
            const row = { machine: d.name || d.serial };
            for (let i = 1; i <= maxCols; i++) {
                row[`c${i}`] = d[`column${i}_units`] ?? 0;
                if (d[`column${i}_status`] !== COLUMN_STATUS.OK) criticalCols.push(`c${i}`);
            }
            row.criticalCols = criticalCols;
            return row;
        })
        .filter((d) => d.criticalCols.length >= 2);

    return { replenishment, watchList, maxCols };
};

const top10By  = (arr, key) => [...arr].sort((a, b) => b[key] - a[key]).slice(0, TOP_N);
const sortDesc = (arr, key) => [...arr].sort((a, b) => b[key] - a[key]);


// Transforms raw /sale/insight-sales response into KPI totals and ranked table data.
// Optionally filters machines by selectedMachineTypes (matched via working_state_id).
const transformInsightsData = (rawData, { selectedMachineTypes, workingStates } = {}) => {
    const machines = Object.entries(rawData)
        .filter(([_, stats]) => {
            if (!selectedMachineTypes || selectedMachineTypes.includes(ALL_TYPES)) return true;
            const stateName = workingStates?.find((s) => s.id === stats[INSIGHT_FIELDS.WORKING_STATE_ID])?.name;
            return selectedMachineTypes.includes(stateName);
        })
        .map(([serial, stats]) => ({
            [MACHINE_KEYS.MACHINE]:       serial,
            [MACHINE_KEYS.SALES]:         stats[INSIGHT_FIELDS.SALES]         || 0,
            [MACHINE_KEYS.SWAPS]:         (stats[INSIGHT_FIELDS.SWAP] || 0) + (stats[INSIGHT_FIELDS.SWAP_PAID] || 0),
            [MACHINE_KEYS.RETURNS]:       stats[INSIGHT_FIELDS.RETURN]        || 0,
            [MACHINE_KEYS.MANUAL_RETURN]: stats[INSIGHT_FIELDS.MANUAL_RETURN] || 0,
            [MACHINE_KEYS.MANUAL_FAIL]:   stats[INSIGHT_FIELDS.MANUAL_FAIL]   || 0,
            [MACHINE_KEYS.FAILS]:         stats[INSIGHT_FIELDS.FAILS]         || 0,
        }));

    const kpi = machines.reduce(
        (acc, m) => ({
            sales:       acc.sales       + m[MACHINE_KEYS.SALES],
            swaps:       acc.swaps       + m[MACHINE_KEYS.SWAPS],
            returns:     acc.returns     + m[MACHINE_KEYS.RETURNS],
            returnFails: acc.returnFails + m[MACHINE_KEYS.FAILS],
        }),
        { sales: 0, swaps: 0, returns: 0, returnFails: 0 }
    );

    const withManualReturn = machines.filter((m) => m[MACHINE_KEYS.MANUAL_RETURN] > 0);
    const withManualFail   = machines.filter((m) => m[MACHINE_KEYS.MANUAL_FAIL]   > 0);
    const withFails        = machines.filter((m) => m[MACHINE_KEYS.FAILS]         > 0);

    return {
        kpi,
        top10Sales: top10By(machines.filter((m) => m[MACHINE_KEYS.SALES] > 0), MACHINE_KEYS.SALES),
        top10Swaps: top10By(machines.filter((m) => m[MACHINE_KEYS.SWAPS] > 0), MACHINE_KEYS.SWAPS),
        manualReturnsSuccess: sortDesc(withManualReturn, MACHINE_KEYS.MANUAL_RETURN).map((m) => ({
            [MACHINE_KEYS.MACHINE]: m[MACHINE_KEYS.MACHINE],
            [MACHINE_KEYS.COUNT]:   m[MACHINE_KEYS.MANUAL_RETURN],
        })),
        manualReturnsFailure: sortDesc(withManualFail, MACHINE_KEYS.MANUAL_FAIL).map((m) => ({
            [MACHINE_KEYS.MACHINE]: m[MACHINE_KEYS.MACHINE],
            [MACHINE_KEYS.COUNT]:   m[MACHINE_KEYS.MANUAL_FAIL],
        })),
        returnFailures: sortDesc(withFails, MACHINE_KEYS.FAILS).map((m) => ({
            [MACHINE_KEYS.MACHINE]:   m[MACHINE_KEYS.MACHINE],
            [MACHINE_KEYS.ERRORS]:    m[MACHINE_KEYS.FAILS],
            [MACHINE_KEYS.ERROR_PCT]: calcFailPct(m[MACHINE_KEYS.RETURNS], m[MACHINE_KEYS.FAILS]),
        })),
    };
};

// Builds the dynamic column slots (C1, C2, …) for replenishment table rows.
const buildReplenishmentCols = (n) =>
    Array.from({ length: n }, (_, i) => ({ header: `C${i + 1}`, accessor: `c${i + 1}` }));

// Builds the dynamic column slots for the watch list, highlighting critical ones in red.
const buildWatchListCols = (n) =>
    Array.from({ length: n }, (_, i) => {
        const col = `c${i + 1}`;
        return {
            header: `C${i + 1}`,
            accessor: col,
            renderCell: (value, row) =>
                row.criticalCols?.includes(col) ? (
                    <span style={{ color: COLORS.error, fontWeight: "bold" }}>{value}</span>
                ) : value,
        };
    });

// ─── Section header ───────────────────────────────────────────────────────────
const SectionHeader = ({ title, subtitle }) => (
    <Row className="mb-1">
        <Col lg="3">
            <h3>{title}</h3>
            {subtitle && (
                <p className="text-muted mb-0" style={{ fontSize: "0.9rem" }}>{subtitle}</p>
            )}
        </Col>
    </Row>
);

// ─── Component ────────────────────────────────────────────────────────────────
const Service = () => {
    const [valueDateRange, setValueDateRange] = useState(() => {
        const now = new Date();
        const endOfToday = new Date(now);
        endOfToday.setHours(23, 59, 59, 999);
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);
        weekAgo.setHours(0, 0, 0, 0);
        return [weekAgo, endOfToday];
    });

    const initialDateRangeRef = useRef(valueDateRange);

    const WORKING_STATES = useWorkingStates();
    const workingStatesRef = useRef(WORKING_STATES);
    useEffect(() => { workingStatesRef.current = WORKING_STATES; }, [WORKING_STATES]);

    const [selectedMachineTypes, setSelectedMachineTypes] = useState([ALL_TYPES]);
    const selectedMachineTypesRef = useRef([ALL_TYPES]);
    useEffect(() => { selectedMachineTypesRef.current = selectedMachineTypes; }, [selectedMachineTypes]);

    const toggleMachineType = (type) => {
        setSelectedMachineTypes((prev) => {
            if (type === ALL_TYPES) return [ALL_TYPES];
            const current = prev.filter((t) => t !== ALL_TYPES);
            const next = current.includes(type)
                ? current.filter((t) => t !== type)
                : [...current, type];
            return next.length === 0 ? [ALL_TYPES] : next;
        });
    };
    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState(null);
    const [maxCols, setMaxCols] = useState(DEFAULT_MAX_COLS);

    const replenishmentColumns = useMemo(() => [
        MACHINE_COL,
        ...buildReplenishmentCols(maxCols),
        { header: "Total", accessor: "total" },
    ], [maxCols]);

    const watchListColumns = useMemo(() => [
        MACHINE_COL,
        ...buildWatchListCols(maxCols),
    ], [maxCols]);

    const fetchData = useCallback(async (startRange, endRange) => {
        setIsLoading(true);
        try {
            const response = await getServiceDashboard({ startRange, endRange });
            const rawData = response.data?.insights || {};
            const realData = transformInsightsData(rawData, { selectedMachineTypes: selectedMachineTypesRef.current, workingStates: workingStatesRef.current });

            const isAllTypes = selectedMachineTypesRef.current.includes(ALL_TYPES);
            const filteredSerials = isAllTypes
                ? null
                : new Set(
                      Object.entries(rawData)
                          .filter(([_, stats]) => {
                              const stateName = workingStatesRef.current?.find(
                                  (s) => s.id === stats[INSIGHT_FIELDS.WORKING_STATE_ID]
                              )?.name;
                              return selectedMachineTypesRef.current.includes(stateName);
                          })
                          .map(([serial]) => serial)
                  );

            const devicesData = response.data?.devices || [];
            const filteredDevicesData = isAllTypes
                ? devicesData
                : devicesData.filter((d) => filteredSerials.has(d.name || d.serial));
            const deviceReport = processDevicesData(filteredDevicesData);
            setMaxCols(deviceReport.maxCols || DEFAULT_MAX_COLS);

            const top10Offline = (response.data?.top_offline?.machines || [])
                .filter((m) => isAllTypes || filteredSerials.has(m[TOP_OFFLINE_FIELDS.NAME]))
                .slice(0, TOP_N)
                .map((m) => ({
                    [MACHINE_KEYS.MACHINE]:              m[TOP_OFFLINE_FIELDS.NAME],
                    [TOP_OFFLINE_FIELDS.OFFLINE_EVENTS]: m[TOP_OFFLINE_FIELDS.OFFLINE_COUNT],
                }));

            setData({
                kpi: realData.kpi,
                top10Sales: realData.top10Sales,
                top10Swaps: realData.top10Swaps,
                top10Offline,
                returnFailures: realData.returnFailures,
                manualReturnsSuccess: realData.manualReturnsSuccess,
                manualReturnsFailure: realData.manualReturnsFailure,
                replenishment: deviceReport.replenishment,
                watchList: deviceReport.watchList,
            });
        } catch (error) {
            console.error("Failed to fetch:", error);
            setData(null);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData(initialDateRangeRef.current[0], initialDateRangeRef.current[1]);
    }, [fetchData]);

    const handleSubmit = () => {
        fetchData(valueDateRange[0], valueDateRange[1]);
    };

    return (
        <div className="content">

            <Row className="mb-3">
                <Col lg="3">
                    <h4>Service</h4>
                </Col>
            </Row>

            <Row className="mb-3 d-flex align-items-stretch">
                <ServiceSubmitDateRange
                    valueDateRange={valueDateRange}
                    setValueDateRange={setValueDateRange}
                    isSubmitting={isLoading}
                />

                <Col xs={12} md="auto">
                    <Card className="h-100">
                        <CardHeader>
                            <CardTitle tag="h4">Machine Type</CardTitle>
                        </CardHeader>
                        <CardBody>
                            <ButtonGroup className="service-machine-type-group">
                                <Button
                                    color={selectedMachineTypes.includes(ALL_TYPES) ? "primary" : "secondary"}
                                    onClick={() => toggleMachineType(ALL_TYPES)}
                                    size="md"
                                    className="btn-fill"
                                    disabled={isLoading}
                                >
                                    {ALL_TYPES}
                                </Button>
                                {WORKING_STATES?.map((type) => (
                                    <Button
                                        key={type.name}
                                        color={selectedMachineTypes.includes(type.name) ? "primary" : "secondary"}
                                        onClick={() => toggleMachineType(type.name)}
                                        size="md"
                                        className="btn-fill"
                                        disabled={isLoading}
                                    >
                                        {type.name}
                                    </Button>
                                ))}
                            </ButtonGroup>
                        </CardBody>
                    </Card>
                </Col>

                <Col xs={12} md="auto" className="d-flex align-items-center service-submit-col">
                    <Button
                        className="btn-fill"
                        color="primary"
                        type="button"
                        onClick={handleSubmit}
                        disabled={isLoading}
                    >
                        {isLoading ? <Spinner size="sm" /> : "Submit"}
                    </Button>
                </Col>
            </Row>

            {isLoading ? (
                <Row>
                    <Col className="text-center py-5">
                        <Spinner />
                    </Col>
                </Row>
            ) : !data ? (
                <Row>
                    <Col className="text-center py-5 text-muted">
                        Failed to load data. Please try again.
                    </Col>
                </Row>
            ) : (
                <>
                    <ServiceKpiPanel kpi={data.kpi} />

                    <SectionHeader title="Rankings Top 10 Machines" />

                    <Row>
                        <ServiceReportTable
                            title="Sales"
                            columns={top10SalesColumns}
                            data={data.top10Sales}
                            theme={TABLE_THEMES.top10Sales}
                            description="Top 10 machines by number of sales (buy/rent) in the selected period."
                        />
                        <ServiceReportTable
                            title="Swaps"
                            columns={top10SwapsColumns}
                            data={data.top10Swaps}
                            theme={TABLE_THEMES.top10Swaps}
                            description="Top 10 machines by number of swap transactions in the selected period."
                        />
                        <ServiceReportTable
                            title="Offline"
                            columns={top10OfflineColumns}
                            data={data.top10Offline}
                            theme={TABLE_THEMES.top10Offline}
                            description="Top 10 machines by number of offline events in the selected period."
                        />
                    </Row>

                    <SectionHeader title="Returns" />
                    <Row>
                        <ServiceReportTable
                            title="Manual returns"
                            columns={manualReturnsColumns}
                            data={data.manualReturnsSuccess}
                            theme={TABLE_THEMES.manualSuccess}
                            description="Machines with successful manual returns (card not recognized, return allowed)."
                            initialLimit={TOP_N}
                        />
                        <ServiceReportTable
                            title="Manual returns – unsuccessful"
                            columns={manualReturnsColumns}
                            data={data.manualReturnsFailure}
                            theme={TABLE_THEMES.returnFailures}
                            description="Machines where manual return attempt failed – device not returned."
                            initialLimit={TOP_N}
                        />
                        <ServiceReportTable
                            title="Return failures"
                            columns={returnFailuresColumns}
                            data={data.returnFailures}
                            theme={TABLE_THEMES.returnFailures}
                            description="Machines with failed return attempts, sorted by failure count."
                            initialLimit={TOP_N}
                        />
                    </Row>

                    <SectionHeader title="Replenishment Status" />
                    <Row>
                        <ServiceReportTable
                            title={`Replenishment total < ${REPLENISHMENT_THRESHOLD}`}
                            columns={replenishmentColumns}
                            data={data.replenishment}
                            theme={TABLE_THEMES.replenishment}
                            description={`Machines with total stock below ${REPLENISHMENT_THRESHOLD} units – require restocking.`}
                            initialLimit={TOP_N}
                            wide
                        />
                    </Row>

                    <Row>
                        <ServiceReportTable
                            title="Watch List – Critical Columns"
                            columns={watchListColumns}
                            data={data.watchList}
                            theme={TABLE_THEMES.watchList}
                            description="Machines with 2 or more columns at critical stock level (status NOK)."
                            initialLimit={TOP_N}
                            wide
                        />
                    </Row>
                </>
            )}
        </div>
    );
};

export default Service;
